import { FC, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { twMerge } from 'tailwind-merge';

import { Country } from '@/shared/api';
import { SearchIcon } from '@/shared/assets';
import { EQueryKeys } from '@/shared/config';
import { ICountry } from '@/shared/lib';
import { Flag, Select, Skeleton } from '@/shared/ui';
import type { SelectOption } from '@/shared/ui/components/select/types';

import { countriesV2Query, defaultCountryQuery } from '../../api';
import { serializeCountry, serializeCountrySelect } from '../../lib';
import { TCountrySelect } from './types';

export const CountrySelect: FC<TCountrySelect> = ({
  value,
  onSelect,
  prefixQueryKey,
  defaultCountry,
  filterIds,
  CustomSkeletons,
  renderOptionItem,
  isPhoneNumber,
  triggerProps,
  isValueCode,
  ...rest
}) => {
  const { data: defaultCountryData, isLoading } = useQuery({
    queryKey: [EQueryKeys.DefaultCountryQueryKey, defaultCountry],
    enabled: Boolean(defaultCountry),
    queryFn: async () => await defaultCountryQuery(defaultCountry ?? ''),
    select: (country) => serializeCountrySelect(country as Country),
  });

  const selectCountryHandler = (
    selectedValue: SelectOption<ICountry> | null,
  ) => {
    onSelect(selectedValue?.option ?? null);
  };

  useEffect(() => {
    if (!defaultCountryData?.option) return;

    if (!value) {
      onSelect(defaultCountryData.option);
    }
  }, [defaultCountryData, onSelect, value]);

  return (
    <Select<ICountry>
      value={
        value
          ? {
              value: isValueCode ? value.code : value.id,
              label: value.name,
              option: value,
            }
          : null
      }
      onSelect={selectCountryHandler}
      loading={isLoading}
      triggerProps={triggerProps}
      CustomSkeletons={
        CustomSkeletons ?? (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <Skeleton className="h-[16px] w-[100px]" />
            </div>
            <Skeleton className="ml-auto h-[20px] w-[50px]" />
          </div>
        )
      }
      renderOptionItem={(item) =>
        renderOptionItem ? (
          renderOptionItem(item)
        ) : (
          <div className="flex w-full items-center gap-2">
            <div className="flex items-center gap-4">
              <Flag alpha2={item.option?.code} className="h-[34px] w-[34px]" />
              <span>{item.option?.name}</span>
            </div>
            <span className="ml-auto text-base-300">{item.option?.alpha3}</span>
          </div>
        )
      }
      modalProps={{
        classNames: {
          wrapper: twMerge(!isPhoneNumber && 'max-h-[min(567px,90dvh)]'),
        },
      }}
      searchProps={{
        placeholder: 'Search a country',
        size: isPhoneNumber ? 'sm' : 'md',
        beforeIcon: isPhoneNumber && <SearchIcon className="size-6" />,
        wrapperClassName: isPhoneNumber ? 'p-5' : 'p-4',
      }}
      queryProps={{
        queryKey: `${prefixQueryKey}-country-select-query-key`,
        queryFn: countriesV2Query,
        serialize: (countries) => {
          const typedCountries = countries as unknown as Country[];
          if (filterIds) {
            return typedCountries
              .filter((country) => filterIds.includes(country.id))
              .map((country) => serializeCountrySelect(country));
          }
          return typedCountries.map((country) =>
            isValueCode
              ? {
                  value: country.item?.code || country.id,
                  label: country.item?.name || country.item?.code || '',
                  option: serializeCountry(country),
                }
              : serializeCountrySelect(country),
          );
        },
      }}
      {...rest}
    />
  );
};
